import * as React from 'react';
import { Plus, Trash2, GripVertical, ChevronDown, ChevronUp } from 'lucide-react';
import { cn } from './utils';
import { Button } from './Button';
import { Input } from './Input';
import { Textarea } from './Textarea';
import { Label } from './Label';
import { ImageUpload } from './ImageUpload';

export interface ListField {
  key: string;
  label: string;
  type: 'text' | 'textarea' | 'image' | 'number' | 'url' | 'email';
  placeholder?: string;
  required?: boolean;
}

export interface ListEditorProps<T extends Record<string, any>> {
  items: T[];
  onChange: (items: T[]) => void;
  fields: ListField[];
  onUpload?: (file: File) => Promise<{ url: string }>;
  maxItems?: number;
  itemLabel?: string;
  defaultItem?: Partial<T>;
  className?: string;
  collapsible?: boolean;
}

export function ListEditor<T extends Record<string, any>>({
  items = [],
  onChange,
  fields,
  onUpload,
  maxItems = 20,
  itemLabel = 'Élément',
  defaultItem = {},
  className,
  collapsible = true,
}: ListEditorProps<T>) {
  const [expandedIndex, setExpandedIndex] = React.useState<number | null>(
    items.length > 0 ? 0 : null
  );
  const [draggedIndex, setDraggedIndex] = React.useState<number | null>(null);

  const handleAddItem = () => {
    if (items.length >= maxItems) return;
    const newItem = { ...defaultItem } as T;
    fields.forEach((field) => {
      if (!(field.key in newItem)) {
        (newItem as any)[field.key] = '';
      }
    });
    const newItems = [...items, newItem];
    onChange(newItems);
    setExpandedIndex(newItems.length - 1);
  };

  const handleRemoveItem = (index: number) => {
    const newItems = items.filter((_, i) => i !== index);
    onChange(newItems);
    if (expandedIndex === index) {
      setExpandedIndex(newItems.length > 0 ? Math.min(index, newItems.length - 1) : null);
    } else if (expandedIndex !== null && expandedIndex > index) {
      setExpandedIndex(expandedIndex - 1);
    }
  };

  const handleUpdateItem = (index: number, key: string, value: any) => {
    const newItems = items.map((item, i) =>
      i === index ? { ...item, [key]: value } : item
    );
    onChange(newItems);
  };

  const handleMoveItem = (fromIndex: number, toIndex: number) => {
    if (toIndex < 0 || toIndex >= items.length) return;
    const newItems = [...items];
    const [movedItem] = newItems.splice(fromIndex, 1);
    newItems.splice(toIndex, 0, movedItem);
    onChange(newItems);
    setExpandedIndex(toIndex);
  };

  const handleDragStart = (index: number) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;
  };

  const handleDrop = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;
    handleMoveItem(draggedIndex, index);
    setDraggedIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  const getItemPreview = (item: T): string => {
    // Try to find a good preview from common field names
    const previewKeys = ['title', 'name', 'question', 'label', 'text', 'content'];
    for (const key of previewKeys) {
      if (item[key] && typeof item[key] === 'string') {
        const value = item[key] as string;
        return value.length > 40 ? value.substring(0, 40) + '...' : value;
      }
    }
    return `${itemLabel} ${items.indexOf(item) + 1}`;
  };

  return (
    <div className={cn('space-y-3', className)}>
      {items.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-4">
          Aucun élément. Cliquez sur "Ajouter" pour commencer.
        </p>
      ) : (
        <div className="space-y-2">
          {items.map((item, index) => (
            <div
              key={index}
              draggable
              onDragStart={() => handleDragStart(index)}
              onDragOver={(e) => handleDragOver(e, index)}
              onDrop={(e) => handleDrop(e, index)}
              onDragEnd={handleDragEnd}
              className={cn(
                'border rounded-lg transition-all',
                draggedIndex === index && 'opacity-50',
                expandedIndex === index ? 'bg-background' : 'bg-muted/30'
              )}
            >
              {/* Header */}
              <div
                className="flex items-center gap-2 p-3 cursor-pointer"
                onClick={() => collapsible && setExpandedIndex(expandedIndex === index ? null : index)}
              >
                <GripVertical className="h-4 w-4 text-muted-foreground cursor-grab" />
                <span className="flex-1 text-sm font-medium truncate">
                  {getItemPreview(item)}
                </span>
                <div className="flex items-center gap-1">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleMoveItem(index, index - 1);
                    }}
                    disabled={index === 0}
                    className="h-7 w-7 p-0"
                  >
                    <ChevronUp className="h-4 w-4" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleMoveItem(index, index + 1);
                    }}
                    disabled={index === items.length - 1}
                    className="h-7 w-7 p-0"
                  >
                    <ChevronDown className="h-4 w-4" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemoveItem(index);
                    }}
                    className="h-7 w-7 p-0 text-destructive hover:text-destructive"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              {/* Content */}
              {(!collapsible || expandedIndex === index) && (
                <div className="px-3 pb-3 space-y-3 border-t">
                  {fields.map((field) => (
                    <div key={field.key} className="space-y-1.5 pt-3">
                      <Label className="text-xs">{field.label}</Label>
                      {field.type === 'textarea' ? (
                        <Textarea
                          value={(item[field.key] as string) || ''}
                          onChange={(e) => handleUpdateItem(index, field.key, e.target.value)}
                          placeholder={field.placeholder}
                          rows={3}
                          className="text-sm"
                        />
                      ) : field.type === 'image' ? (
                        onUpload ? (
                          <ImageUpload
                            value={(item[field.key] as string) || ''}
                            onChange={(url) => handleUpdateItem(index, field.key, url)}
                            onUpload={onUpload}
                            aspectRatio="1:1"
                            placeholder={field.placeholder || 'Télécharger une image'}
                          />
                        ) : (
                          <Input
                            type="url"
                            value={(item[field.key] as string) || ''}
                            onChange={(e) => handleUpdateItem(index, field.key, e.target.value)}
                            placeholder={field.placeholder || 'URL de l\'image'}
                            className="text-sm"
                          />
                        )
                      ) : (
                        <Input
                          type={field.type === 'number' ? 'number' : field.type === 'email' ? 'email' : field.type === 'url' ? 'url' : 'text'}
                          value={(item[field.key] as string) || ''}
                          onChange={(e) => handleUpdateItem(index, field.key, field.type === 'number' ? parseFloat(e.target.value) : e.target.value)}
                          placeholder={field.placeholder}
                          className="text-sm"
                        />
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={handleAddItem}
        disabled={items.length >= maxItems}
        className="w-full"
      >
        <Plus className="h-4 w-4 mr-2" />
        Ajouter {itemLabel.toLowerCase()}
      </Button>

      {items.length >= maxItems && (
        <p className="text-xs text-muted-foreground text-center">
          Maximum {maxItems} éléments atteint
        </p>
      )}
    </div>
  );
}
